import type { DropResult } from '@hello-pangea/dnd';
import type { FunctionComponent } from 'react';
import { Fragment } from 'react';
import type { FieldErrors, UseFormReturn } from 'react-hook-form';

import { useTranslate } from '~/shared/context/use-translate';
import type { Competencies, JobProfileComponent } from '~/shared/data-contract/job-profile';
import type { JobProfileSkill, SkillProficiencyLevel } from '~/shared/data-contract/skill';

import { Section, SectionContent, SectionHeader } from '../components/Common';
import { CategoryListSkeleton, SkillsAiSuggestionsSkeleton } from '../components/SkeletonLoaders';
import { EditSkillsAndCompetenciesOptions } from './EditSkillsAndCompetenciesOptions';
import type { RegenerationState } from './RegenerateDialog';
import { SkillsAiSuggestions } from './SkillsAiSuggestions';
import { SkillsSectionOptions } from './SkillsSectionOptions';
import { CategoryList } from './skills/CategoryList';
import { addSkillsToCompetencies, mergeCompetenciesFromSkills } from './skills/use-skills-with-compentencies';
import type { CompetencyWithSkills, ProfileForm } from './types';

type SkillsSectionProps = {
    readonly allCompetencies: Competencies | undefined;
    readonly architectureId: string;
    readonly commentable: boolean;
    readonly newThreadCreationDisabled?: boolean;
    readonly competenciesWithSkills: readonly CompetencyWithSkills[];
    readonly currentSkillCount: number;
    readonly formErrors: FieldErrors<ProfileForm>;
    readonly formMethods: UseFormReturn<ProfileForm>;
    readonly formSkills: readonly JobProfileSkill[];
    readonly hasFormChanges: boolean;
    readonly isEditing: boolean;
    readonly isGeneratingSkills: boolean;
    readonly isSkillLimitReached: boolean;
    readonly itemId?: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly maximumSkillLimit: number | undefined;
    readonly onChangeProficiencyLevel: (skillId: string, level: JobProfileSkill['level']) => void;
    readonly onManualEdit: () => void;
    readonly onMove: (result: DropResult<string>) => void;
    readonly onOpenSkillDetails: (skillId: string, proficiency: SkillProficiencyLevel | null | undefined) => void;
    readonly onRegenerateDialogOpen: () => void;
    readonly onRemoveCompetency: (competencyId: string) => void;
    readonly onRemoveSkill: (competencyId: string, skillId: string) => void;
    readonly profile: JobProfileComponent;
    readonly regenerationState: RegenerationState;
};

export const SkillsSection: FunctionComponent<SkillsSectionProps> = ({
    allCompetencies,
    architectureId,
    commentable,
    newThreadCreationDisabled = false,
    competenciesWithSkills,
    currentSkillCount,
    formErrors,
    formMethods,
    formSkills,
    hasFormChanges,
    isEditing,
    isGeneratingSkills,
    isSkillLimitReached,
    itemId,
    itemType,
    maximumSkillLimit,
    onChangeProficiencyLevel,
    onManualEdit,
    onMove,
    onOpenSkillDetails,
    onRegenerateDialogOpen,
    onRemoveCompetency,
    onRemoveSkill,
    profile,
    regenerationState,
}) => {
    const translate = useTranslate();

    return (
        <Section>
            <SectionHeader>
                {translate('pages.job-architecture-v2.job-profile-required-skills')}
                <SkillsSectionOptions />
                {isEditing && (
                    <EditSkillsAndCompetenciesOptions
                        architectureId={architectureId}
                        control={formMethods.control}
                        currentSkillCount={currentSkillCount}
                        hasUnsavedChanges={hasFormChanges}
                        isSkillLimitReached={isSkillLimitReached}
                        itemType={itemType}
                        maximumSkillLimit={maximumSkillLimit}
                        onCompetencyChange={onManualEdit}
                        onRegenerate={onRegenerateDialogOpen}
                        onSkillChange={onManualEdit}
                    />
                )}
            </SectionHeader>
            <SectionContent>
                {regenerationState.includes('SKILLS') && (
                    // biome-ignore lint/complexity/noUselessFragments: suppressed due to migration
                    <Fragment>
                        <SkillsAiSuggestionsSkeleton />
                    </Fragment>
                )}
                {isGeneratingSkills ? (
                    // biome-ignore lint/complexity/noUselessFragments: suppressed due to migration
                    <Fragment>
                        <CategoryListSkeleton />
                    </Fragment>
                ) : (
                    <Fragment>
                        <SkillsAiSuggestions
                            architectureId={architectureId}
                            currentSkills={formSkills}
                            profile={profile}
                            onAcceptSkillSuggestions={(skills: JobProfileSkill[]): void => {
                                formMethods.setValue('skills', skills);

                                const currentCompetencies = formMethods.getValues('competencies');
                                const baseCompetencies = mergeCompetenciesFromSkills(
                                    currentCompetencies,
                                    skills,
                                    allCompetencies,
                                );

                                formMethods.setValue('competencies', addSkillsToCompetencies(baseCompetencies, skills));
                                onManualEdit();
                            }}
                        />
                        <CategoryList
                            commentable={commentable}
                            competenciesWithSkills={competenciesWithSkills}
                            formErrors={formErrors}
                            handleOnDragEnd={onMove}
                            isInEditMode={isEditing}
                            itemId={itemId}
                            newThreadCreationDisabled={newThreadCreationDisabled}
                            onChangeProficiencyLevel={onChangeProficiencyLevel}
                            onOpenSkillDetails={onOpenSkillDetails}
                            onRemoveCompetency={onRemoveCompetency}
                            onRemoveSkill={onRemoveSkill}
                            onSkillChange={onManualEdit}
                        />
                    </Fragment>
                )}
            </SectionContent>
        </Section>
    );
};
